import React, {useEffect, useState} from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import WebView from 'react-native-webview';
import {useFocusEffect} from '@react-navigation/native';
import {ThemeProvider} from '@rn-vui/themed';
import {observer} from 'mobx-react-lite';

import {ProductCarousel} from '../../components/ProductCarousel';
import ActivityIndicator from '../../components/ui-controls/ActivityIndicator';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Markdown from '../../components/ui-controls/Markdown';
import Text from '../../components/ui-controls/Text';
import {useTabBarBottomPadding} from '../../hooks/useTabBarBottomPadding';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {
  AirtableAttachmentDetail,
  AirtableFeedItem,
  AirtableFeedItemEmbeddedJs,
} from '../../models/airtable';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useLinkTrailBack} from '../../navigation/hooks/useLinkTrailBack';
import {useResolveDiscoverArticle} from '../../queries/feed/useResolveDiscoverArticle';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import rewardsTheme from '../../theme/rewardsTheme';
import {OrderLine} from '../../transport/apiServiceResponse';
import {ArticleVideoType} from '../../utils/feedAndDiscover/constants';
import {getFeedDiscoverDetailGraphics} from '../../utils/feedAndDiscover/media';
import {openPageLink} from '../../utils/navigation';

const STUB_RESOLVE_TIMEOUT_MS = 5000;

const FeedDiscoverDetailScreen = observer(
  ({navigation, route}: RouteNavigatorScreenProps<AppRoute.ARTICLE>) => {
    const {feedStore, shopSelectionStore} = useStores();
    const {isWebDesktopScreen} = useWebScreenDimensions();
    // DIS-1226: AnimatedTabBar is absolute-positioned on web mobile and overlays the
    // bottom ~52 px of this ScrollView, hiding the last linked-product tile's
    // Add-to-Cart button. Hook returns 0 on native and web desktop. Mirrors DIS-1127
    // fix on PromotionDetailScreen.
    const tabBarPadding = useTabBarBottomPadding();

    const [headlineVideoPaused, setHeadlineVideoPaused] = useState(false);
    const [articleVideoPaused, setArticleVideoPaused] = useState(true);
    const [youtubeVideoPaused, setYoutubeVideoPaused] = useState(true);
    const [linkedProducts, setLinkedProducts] = useState<OrderLine[]>([]);
    const [isLoadingLinkedProducts, setIsLoadingLinkedProducts] = useState(false);

    const target = navigation.getId() as AppRoute;

    // Android hardware back: follow the in-page link trail, not the tab history.
    useLinkTrailBack(route.params);

    const rawArticle = route.params?.article;
    const isPendingStub =
      !!rawArticle && '__pending' in rawArticle && rawArticle.__pending === true;

    // DIS-1516: resolve cold-start stubs from TanStack ALL-segment cache (not
    // feedStore.discoverList — empty after DIS-1344).
    const {article: resolvedFromCache, status: resolveStatus} = useResolveDiscoverArticle({
      url: isPendingStub ? rawArticle.url : undefined,
      enabled: isPendingStub,
    });
    const resolvedArticle: AirtableFeedItem | undefined = isPendingStub
      ? resolvedFromCache
      : (rawArticle as AirtableFeedItem | undefined);

    // Effect 1: immediate redirect when no article param at all (preserves existing behaviour).
    useEffect(() => {
      if (!route.params?.article) navigation.navigate(AppRoute.HOME);
    }, [route.params?.article, navigation]);

    // Stub resolve finished with no match (or query error) → Home.
    useEffect(() => {
      if (resolveStatus === 'not_found' || resolveStatus === 'error') {
        navigation.navigate(AppRoute.HOME);
      }
    }, [resolveStatus, navigation]);

    // Safety timeout — only while still loading (e.g. store never hydrates).
    // Clears on found / not_found / error / unmount.
    useEffect(() => {
      if (resolveStatus !== 'loading') return;
      const timer = setTimeout(() => {
        navigation.navigate(AppRoute.HOME);
      }, STUB_RESOLVE_TIMEOUT_MS);
      return () => clearTimeout(timer);
    }, [resolveStatus, navigation]);

    useEffect(() => {
      if (resolvedArticle?.productBarcodes) {
        (async function () {
          setLinkedProducts([]);
          setIsLoadingLinkedProducts(true);
          const linked = await feedStore.loadLinkedProducts(resolvedArticle);
          setLinkedProducts(linked as OrderLine[]);
          setIsLoadingLinkedProducts(false);
        })();
      }
    }, [resolvedArticle, feedStore, shopSelectionStore.selectedStore]);

    // This is to stop the video playing whenever we navigate away.
    useFocusEffect(
      React.useCallback(() => {
        return () => {
          setHeadlineVideoPaused(true);
          setArticleVideoPaused(true);
          setYoutubeVideoPaused(true);
        };
      }, []),
    );

    const renderEmbeddedJSVideo = ({src, web, mobile}: AirtableFeedItemEmbeddedJs) => {
      const platformSpecificSrc = isWebDesktopScreen ? web?.src : mobile?.src;
      const html = platformSpecificSrc || src;

      let width = isWebDesktopScreen ? web?.width : mobile?.width;
      let height = isWebDesktopScreen ? web?.height : mobile?.height;
      width = width || '100%';
      height = height || '100%';

      const style = StyleSheet.create({
        main: {
          width,
          height,
        },
      });

      return (
        <WebView
          style={style.main}
          originWhitelist={['*']}
          source={{html}}
          onShouldStartLoadWithRequest={req => {
            const {url} = req;
            // Allow the injected HTML bootstrap itself; source={{html}} fires
            // about:/data: on the initial load.
            if (!url || url.startsWith('about:') || url.startsWith('data:')) return true;
            // Own-domain -> in-app NAVIGATE via openPageLink; foreign -> OS
            // browser. Returning false keeps the WebView mounted and neutral.
            openPageLink(url);
            return false;
          }}
        />
      );
    };

    const getImage = (
      detail: AirtableAttachmentDetail | undefined,
      vidType: ArticleVideoType,
      margin?: boolean,
    ) =>
      getFeedDiscoverDetailGraphics(
        detail,
        vidType,
        margin,
        resolvedArticle,
        // @ts-expect-error // TODO: fix this
        setHeadlineVideoPaused,
        headlineVideoPaused,
        articleVideoPaused,
        youtubeVideoPaused,
      );

    // Pending stub / absent article — spinner while resolving from TanStack
    // (or briefly before Home redirect on not_found / error / timeout).
    if (!resolvedArticle) {
      return (
        <ThemeProvider theme={rewardsTheme}>
          <ContentView>
            <ActivityIndicator />
          </ContentView>
        </ThemeProvider>
      );
    }

    return (
      <ThemeProvider theme={rewardsTheme}>
        <ContentView>
          <ScrollView
            style={{flex: 1}}
            showsVerticalScrollIndicator={false}
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{paddingBottom: tabBarPadding}}>
            {getImage(resolvedArticle?.heroImage, ArticleVideoType.HeadlineVideo)}
            <View style={styles.textCard}>
              <Text style={styles.headline}>{resolvedArticle?.headline}</Text>
              <Markdown>{resolvedArticle.firstCopy?.trimEnd()}</Markdown>
            </View>
            {getImage(resolvedArticle?.articleImage, ArticleVideoType.ArticleVideo, true)}
            {!!(target === AppRoute.HOME && resolvedArticle?.secondCopy) && (
              <View style={styles.textCard}>
                <Markdown>{resolvedArticle.secondCopy?.trimEnd()}</Markdown>
              </View>
            )}
            <View style={styles.separator} />
            {!!resolvedArticle?.youtube && getImage({type: 'video'}, ArticleVideoType.YoutubeVideo)}
            {!!resolvedArticle?.embeddedJs && renderEmbeddedJSVideo(resolvedArticle.embeddedJs)}
            {!!(target === AppRoute.DISCOVER && resolvedArticle?.secondCopy) && (
              <View style={styles.textCard}>
                <Markdown>{resolvedArticle.secondCopy?.trimEnd()}</Markdown>
              </View>
            )}
            {!shopSelectionStore.ecommerceDisabled &&
              resolvedArticle?.productBarcodes?.length > 0 &&
              (isLoadingLinkedProducts ? (
                <ContentView>
                  <ActivityIndicator />
                </ContentView>
              ) : (
                <View style={styles.productList}>
                  <ProductCarousel data={linkedProducts} />
                </View>
              ))}
          </ScrollView>
        </ContentView>
      </ThemeProvider>
    );
  },
);

export default FeedDiscoverDetailScreen;

const styles = StyleSheet.create({
  separator: {
    marginBottom: 16,
  },
  textCard: {
    marginTop: 16,
  },
  headline: {
    fontSize: 20,
    color: theme.colour.text.heading.primary,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  copy: {
    fontSize: 16,
  },
  productList: {
    marginTop: 24,
    marginBottom: 4,
  },
});
